// What the buyer changed on the 訂貨 screen BEFORE he sends the order — and nothing more.
//
// Magnus 2026-09-17, on 訂貨: 「why i uncheck it disappear? and i can't check back」 and 「also on
// this page, make it easy for me to change the variant to order」.
//
// The first was my design error. The tick beside an order line was wired to `veto.excludeStyle`,
// which POSTs /restock-v2/exclude scope='plan' — and since the counter list shipped, that lands in
// `noke_counter_list` as `status='rejected'`. So unticking a line on the ORDER screen permanently
// rejected the style from his counter list, the next recompute dropped it out of `order.groups`
// entirely, and the card unmounted: there was no tick left to tick back. The control had not
// failed, it had removed itself. (Reproduced 2026-09-18 before this module existed.)
//
// So there are now two verbs on an order row, and they mean different things:
//   the TICK      「這次不訂」 — this order only. Nothing is written anywhere. Reversible by ticking.
//   不要這款      the standing veto — still the same /exclude call, still shared with Adam, still
//                 permanent. It is drawn heavier because it IS heavier.
//
// WHERE THIS STATE LIVES, and why (the storage decision, made deliberately):
//   In React state on the tab. NOT the server: that is what made the tick a trap. NOT sessionStorage
//   keyed by `generated_at` either — that is what 訂貨 used before 2026-09-15, and it is now actively
//   worse than it was then, because `generated_at` changes on every recompute and a recompute now
//   lands 1.5s after any write, so the key would rotate out from under him mid-order. The 09-15
//   reasoning (「it vanished on his phone and Adam never saw it」) is about STANDING decisions, which
//   is exactly what 不要這款 still is. 「not in this order」 is transient by nature: it belongs to the
//   one order being assembled, in the one tab assembling it, and it must survive a plan refresh in
//   that session — which tab state does and the rotating sessionStorage key would not.
//
// WHY ONE MODULE RATHER THAN THREE CALL SITES: the edit has to reach the copy text, the on-screen
// totals and the /ordered payload IDENTICALLY. A number he reads that is not the number he pastes
// is the worst outcome on a screen where he is about to spend money. So the edit is applied ONCE,
// to the plan (`applyOrderEdit`), and every existing consumer keeps reading the plan as before —
// `orderCopyText`, `tierTotalKept`, `koreaTotalKept`, `groupPiecesKept`, `koreaOrderedLines`. There
// is no second path for a quantity to travel down and therefore no way for two of them to disagree.
import { buildOrderCopyText } from './orderPlan';
import {
  RestockV2Order, RestockV2OrderGroup, RestockV2OrderLine, RestockV2OrderVariant, RestockV2Plan,
  TierKey,
} from './types';

export interface OrderEdit {
  // SKU → 「這次不訂」. Per-order only; no request, no server state, no other screen sees it.
  omitted: Record<string, true>;
  // `sku|variant_id` → the quantity he set. The sku+variant PAIR is the key, never the SKU alone —
  // the same rule the veto and the 選品 chips follow, and for the same reason: one style has many
  // colours and a SKU-keyed override would move all of them at once.
  qty: Record<string, number>;
  // `sku|variant_id` → a colour/size the ENGINE did not put on the line, which he added by hand.
  //
  // Magnus 2026-09-18: 「there is still no way to edit to a specific size and 尺寸 that wasn't
  // shown」. The steppers only ever listed `line.variants`, and the backend builds that array from
  // the variants with a GAP (noke-restock-v2-plan.js:196 — `target − on_hand − transit − ordered`),
  // so an XL the counter already covers, or one the engine gave no target at all, simply is not on
  // the line and had no control anywhere. The whole point of this screen is that he decides, so a
  // colour he cannot reach is a hole.
  //
  // The value is a WHOLE RestockV2OrderVariant with `order_qty: 0` — not a marker — precisely so
  // that it is inserted into `line.variants` in `applyOrderEdit` and then travels the SAME path as
  // every engine-chosen row: the chip, `lineQty`, the group/韓國 totals, the tab badge, the copy
  // text and the /ordered body all read it off the plan and cannot disagree about it. And 0 being
  // the engine's number for it means 「back to 0」 clears the qty override exactly as it does
  // anywhere else — one verb for dropping a colour, still.
  added: Record<string, RestockV2OrderVariant>;
}

export const EMPTY_ORDER_EDIT: OrderEdit = { omitted: {}, qty: {}, added: {} };

/** Nothing above this per colour/size. A slip on a stepper should not become a 900-piece order. */
export const MAX_VARIANT_QTY = 99;

export function orderVariantKey(sku: string, variantId: number | null | undefined): string {
  return `${sku}|${variantId === null || variantId === undefined ? '' : variantId}`;
}

export function orderEditIsEmpty(edit: OrderEdit): boolean {
  return !Object.keys(edit.omitted).length && !Object.keys(edit.qty).length
    && !Object.keys(edit.added || {}).length;
}

/** The colours/sizes he added to ONE style, in the order he added them. */
export function addedVariantsOf(edit: OrderEdit, sku: string): RestockV2OrderVariant[] {
  const prefix = `${sku}|`;
  return Object.keys(edit.added || {})
    .filter((key) => key.indexOf(prefix) === 0)
    .map((key) => edit.added[key]);
}

export function isAddedVariant(
  edit: OrderEdit, sku: string, variantId: number | null | undefined,
): boolean {
  return (edit.added || {})[orderVariantKey(sku, variantId)] !== undefined;
}

/**
 * The line's variants as he has them: the engine's, then anything he added that the engine has not
 * since picked up itself.
 *
 * The dedupe on `variant_id` is load-bearing across a recompute: he adds XL, the engine's next run
 * decides XL needs ordering too and puts it on the line — without this the row would appear twice
 * and the two copies would each contribute to the total.
 */
function lineVariants(edit: OrderEdit, line: RestockV2OrderLine): RestockV2OrderVariant[] {
  const own = line.variants || [];
  const extra = addedVariantsOf(edit, line.sku)
    .filter((variant) => !own.some((row) => row.variant_id === variant.variant_id));
  return extra.length ? own.concat(extra) : own;
}

/**
 * 加入這個顏色／尺寸. It starts at 0 and is then an ordinary stepper row — no second mechanism, no
 * second number. Adding twice is a no-op rather than a reset, so a double tap cannot wipe a count.
 */
export function addVariant(
  edit: OrderEdit, sku: string, variant: RestockV2OrderVariant,
): OrderEdit {
  const key = orderVariantKey(sku, variant.variant_id);
  if ((edit.added || {})[key]) return edit;
  return { ...edit, added: { ...edit.added, [key]: { ...variant, order_qty: 0 } } };
}

/** Take a hand-added colour back off the line — the row AND any quantity he typed into it. */
export function removeAddedVariant(
  edit: OrderEdit, sku: string, variantId: number | null | undefined,
): OrderEdit {
  const key = orderVariantKey(sku, variantId);
  const added = { ...edit.added };
  const qty = { ...edit.qty };
  delete added[key];
  delete qty[key];
  return { ...edit, added, qty };
}

export function isOmitted(edit: OrderEdit, sku: string): boolean {
  return edit.omitted[sku] === true;
}

/** 這次不訂 / 這次要訂. A pure toggle — the caller writes it to state and the screen moves at once. */
export function toggleOrderLine(edit: OrderEdit, sku: string): OrderEdit {
  const omitted = { ...edit.omitted };
  if (omitted[sku]) delete omitted[sku]; else omitted[sku] = true;
  return { ...edit, omitted };
}

/**
 * 全部取消 / 全部恢復 — Magnus 2026-09-20: 「make a button that will allow me to deselect all」.
 *
 * ⚠️ This is NOT the 全選／全部取消 that was removed from 訂貨 on 2026-09-15. That one drove
 * 不要這款, which writes a SHARED veto per style, so one mis-tap vetoed the whole order for Adam
 * too and kept it vetoed. This writes `omitted`, which is 這次不訂: local to his screen, gone on
 * refresh, and undone by the same button. Different verb, different blast radius.
 *
 * `skus` is what the screen is SHOWING, so a filtered view cancels what he can see and nothing
 * else — silently cancelling rows scrolled off the page is how a bulk control earns distrust.
 */
export function omitAllLines(edit: OrderEdit, skus: string[]): OrderEdit {
  const omitted = { ...edit.omitted };
  (skus || []).forEach((sku) => { omitted[sku] = true; });
  return { ...edit, omitted };
}

/** The undo. Clears only the rows named, for the same reason. */
export function restoreAllLines(edit: OrderEdit, skus: string[]): OrderEdit {
  const omitted = { ...edit.omitted };
  (skus || []).forEach((sku) => { delete omitted[sku]; });
  return { ...edit, omitted };
}

/** Clamped, integer, and 0 is a real answer — 0 IS how a colour is dropped, so there is one verb. */
export function clampQty(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const whole = Math.round(value);
  if (whole < 0) return 0;
  return whole > MAX_VARIANT_QTY ? MAX_VARIANT_QTY : whole;
}

export function setVariantQty(
  edit: OrderEdit, sku: string, variant: RestockV2OrderVariant, next: number,
): OrderEdit {
  const key = orderVariantKey(sku, variant.variant_id);
  const value = clampQty(next);
  const qty = { ...edit.qty };
  // Back to exactly what the engine chose = no override at all, so the row stops reading 已改 and
  // the plan's own number is used again. An override equal to the engine's is not a change.
  if (value === (Number(variant.order_qty) || 0)) delete qty[key];
  else qty[key] = value;
  return { ...edit, qty };
}

/**
 * 還原這款 — put the engine's own line back, leaving its 這次不訂 tick alone.
 *
 * That means the hand-added rows go too: 還原數量 says 「what the engine proposed」, and a line still
 * carrying a colour the engine never chose is not that line, even at 0.
 */
export function resetLineQty(edit: OrderEdit, line: RestockV2OrderLine): OrderEdit {
  const qty = { ...edit.qty };
  const added = { ...edit.added };
  lineVariants(edit, line).forEach((variant) => {
    const key = orderVariantKey(line.sku, variant.variant_id);
    delete qty[key];
    delete added[key];
  });
  return { ...edit, qty, added };
}

/** What will actually be ordered for one colour/size: his number when he set one, else the engine's. */
export function variantQty(
  edit: OrderEdit, sku: string, variant: RestockV2OrderVariant,
): number {
  const key = orderVariantKey(sku, variant.variant_id);
  const own = edit.qty[key];
  return own === undefined ? (Number(variant.order_qty) || 0) : own;
}

/**
 * A blocked colour is not editable. `block_reason` is the engine saying the source cannot supply it
 * (or the counter already holds it); a stepper there would let him type a number that the order can
 * never satisfy, which is the same class of lie as a total that does not match the copy text.
 */
export function variantEditable(variant: RestockV2OrderVariant): boolean {
  return !(variant.block_reason || '').trim();
}

export function lineIsEdited(edit: OrderEdit, line: RestockV2OrderLine): boolean {
  return lineVariants(edit, line).some((variant) => {
    const key = orderVariantKey(line.sku, variant.variant_id);
    return edit.qty[key] !== undefined || (edit.added || {})[key] !== undefined;
  });
}

/** The line's own piece count after his edits. A style with no variant rows keeps the engine's. */
export function lineQty(edit: OrderEdit, line: RestockV2OrderLine): number {
  const variants = lineVariants(edit, line);
  if (!variants.length) return Number(line.order_qty) || 0;
  return variants.reduce((sum, variant) => sum + variantQty(edit, line.sku, variant), 0);
}

function editLine(edit: OrderEdit, line: RestockV2OrderLine): RestockV2OrderLine {
  const variants = lineVariants(edit, line).map((variant) => ({
    ...variant, order_qty: variantQty(edit, line.sku, variant),
  }));
  return { ...line, variants, order_qty: lineQty(edit, line) };
}

function editLines(edit: OrderEdit, lines?: RestockV2OrderLine[]): RestockV2OrderLine[] {
  return (lines || []).map((line) => editLine(edit, line));
}

const piecesOf = (lines: RestockV2OrderLine[]): number => lines
  .reduce((sum, line) => sum + (Number(line.order_qty) || 0), 0);

/**
 * The plan as the buyer has edited it.
 *
 * Every derived number in `order.totals` and every `group.pieces` is RECOMPUTED here rather than
 * left as the server sent it, because `orderCopyText` / `tierTotalKept` / `koreaTotalKept` /
 * `groupPiecesKept` all short-circuit to the server's own figures when nothing is excluded. Leaving
 * them would print the engine's totals above an order carrying his quantities — the exact mismatch
 * this screen must never show. `copy_text` is rebuilt for the same reason: with quantities edited
 * but nothing unticked, the untouched short-circuit would have handed him the server's string.
 *
 * `omitted` is deliberately NOT applied here: it rides the existing `excluded` argument that every
 * one of those functions already takes, so a style taken out of this order and a style vetoed for
 * good leave the copy, the totals and the payload through the same door.
 */
export function applyOrderEdit(plan: RestockV2Plan | null, edit: OrderEdit): RestockV2Plan | null {
  if (!plan || !plan.order) return plan;
  if (!Object.keys(edit.qty).length && !Object.keys(edit.added || {}).length) return plan;
  const order = plan.order;
  const groups: RestockV2OrderGroup[] = (order.groups || []).map((group) => {
    const lines = editLines(edit, group.lines);
    return { ...group, lines, pieces: piecesOf(lines) };
  });
  const tiers: RestockV2Order['tiers'] = {};
  (Object.keys(order.tiers || {}) as TierKey[]).forEach((tier) => {
    tiers[tier] = editLines(edit, order.tiers?.[tier]);
  });
  const warehouse = editLines(edit, order.warehouse);
  const korea = groups.reduce((sum, group) => sum + group.pieces, 0);
  const totals: RestockV2Order['totals'] = {
    ...order.totals,
    core: piecesOf(tiers.core || []),
    lead: piecesOf(tiers.lead || []),
    korea,
    warehouse: piecesOf(warehouse),
  };
  if (order.totals?.new_season !== undefined) totals.new_season = piecesOf(tiers.new_season || []);
  if (order.totals?.seasonal !== undefined) totals.seasonal = piecesOf(tiers.seasonal || []);
  const edited: RestockV2Plan = {
    ...plan,
    order: { ...order, groups, tiers, warehouse, totals, copy_text: '' },
  };
  return {
    ...edited,
    order: { ...edited.order, copy_text: buildOrderCopyText(edited, undefined) },
  };
}

/**
 * Everything that must leave this order: what he unticked, plus whatever 不要這款 is still writing.
 *
 * One merged set, because the three consumers take exactly one `excluded` argument — so a piece
 * cannot be in the copy text and out of the totals, whichever of the two verbs took it out.
 */
export function notOrdered(
  edit: OrderEdit, vetoing: Record<string, true>,
): Record<string, true> {
  return { ...vetoing, ...edit.omitted };
}

/** 「這次不訂 2 款 · 改了 3 款的數量」, or empty when he has changed nothing. */
export function orderEditLine(edit: OrderEdit): string {
  const off = Object.keys(edit.omitted).length;
  const added = Object.keys(edit.added || {});
  // A hand-added row he has not stepped up yet carries no quantity override, so counting only
  // `qty` would report 「改了 0 個」 on a screen that is visibly showing a row he just added.
  const changed = Object.keys(edit.qty).filter((key) => added.indexOf(key) < 0).length;
  const parts: string[] = [];
  if (off) parts.push(`這次不訂 ${off} 款`);
  if (changed) parts.push(`改了 ${changed} 個顏色／尺寸的數量`);
  if (added.length) parts.push(`加了 ${added.length} 個顏色／尺寸`);
  return parts.join(' · ');
}
